import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ArrowForward, Business } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { AdminCompanyListItem, PaginatedResponse, SearchParams, SubscriptionStatus } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { errorMessage } from '../../utils/errorMessage';
import PageIconTile from '../common/PageIconTile';

type AttentionKey = 'expiring' | 'restricted' | 'inactive' | 'neverUsed';

interface CompanyAttentionListProps {
  cacheKey: string;
}

const page = (params: SearchParams): Promise<PaginatedResponse<AdminCompanyListItem>> =>
  apiClient.getAdminCompaniesPaginated({ page: 0, size: 5, direction: 'asc', ...params });

const formatDate = (value?: string | null): string => value ? new Date(value).toLocaleDateString() : '—';

const CompanyAttentionList: React.FC<CompanyAttentionListProps> = ({ cacheKey }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<AttentionKey>('expiring');
  const queries = useQueries({
    queries: [
      {
        queryKey: ['admin', 'companies', 'attention', 'expiring', cacheKey],
        queryFn: () => page({ expiresWithinDays: 7, sort: 'endsAt' }),
      },
      {
        queryKey: ['admin', 'companies', 'attention', 'restricted-read-only', cacheKey],
        queryFn: () => page({ effectiveStatus: SubscriptionStatus.READ_ONLY, sort: 'endsAt' }),
      },
      {
        queryKey: ['admin', 'companies', 'attention', 'restricted-suspended', cacheKey],
        queryFn: () => page({ effectiveStatus: SubscriptionStatus.SUSPENDED, sort: 'endsAt' }),
      },
      {
        queryKey: ['admin', 'companies', 'attention', 'inactive', cacheKey],
        queryFn: () => page({ usageStatus: 'INACTIVE_30D', sort: 'lastBusinessActivityAt', direction: 'asc' }),
      },
      {
        queryKey: ['admin', 'companies', 'attention', 'never-used', cacheKey],
        queryFn: () => page({ usageStatus: 'NEVER_USED', sort: 'createdAt', direction: 'desc' }),
      },
    ],
  });

  const [expiringQuery, readOnlyQuery, suspendedQuery, inactiveQuery, neverUsedQuery] = queries;
  const restrictedRows = useMemo(() => {
    const rows = [...(readOnlyQuery.data?.content || []), ...(suspendedQuery.data?.content || [])];
    return rows.sort((left, right) => {
      const leftEndsAt = left.subscription?.endsAt ? new Date(left.subscription.endsAt).getTime() : Number.POSITIVE_INFINITY;
      const rightEndsAt = right.subscription?.endsAt ? new Date(right.subscription.endsAt).getTime() : Number.POSITIVE_INFINITY;
      return leftEndsAt - rightEndsAt || left.name.localeCompare(right.name);
    });
  }, [readOnlyQuery.data, suspendedQuery.data]);

  const selectedQuery = selected === 'expiring'
    ? expiringQuery
    : selected === 'inactive'
      ? inactiveQuery
      : selected === 'neverUsed'
        ? neverUsedQuery
        : undefined;
  const rows = selected === 'restricted' ? restrictedRows : (selectedQuery?.data?.content || []);
  const isLoading = selected === 'restricted'
    ? readOnlyQuery.isLoading || suspendedQuery.isLoading
    : Boolean(selectedQuery?.isLoading);
  const error = selected === 'restricted'
    ? readOnlyQuery.error || suspendedQuery.error
    : selectedQuery?.error;
  const total = selected === 'restricted'
    ? (readOnlyQuery.data?.totalElements || 0) + (suspendedQuery.data?.totalElements || 0)
    : selectedQuery?.data?.totalElements || 0;
  const label = (key: AttentionKey): string => {
    const labels: Record<AttentionKey, string> = {
      expiring: t('dashboard.attentionExpiring'),
      restricted: t('dashboard.attentionRestricted'),
      inactive: t('dashboard.attentionInactive'),
      neverUsed: t('dashboard.attentionNeverUsed'),
    };
    return labels[key];
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, mt: 1.5, borderRadius: 2.5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
        <Stack direction="row" spacing={1.25} alignItems="center">
            <PageIconTile icon={<Business />} accent="companies" size={38} iconSize={21} />
          <Box>
            <Typography variant="h6">{t('dashboard.attentionTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('dashboard.attentionHint')}
            </Typography>
          </Box>
        </Stack>
        <Button endIcon={<ArrowForward />} onClick={() => navigate('/companies')}>
          {t('dashboard.viewCompanies')}
        </Button>
      </Stack>
      <Tabs
        value={selected}
        onChange={(_, value: AttentionKey) => setSelected(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mt: 1 }}
      >
        {(['expiring', 'restricted', 'inactive', 'neverUsed'] as AttentionKey[]).map(key => (
          <Tab key={key} value={key} label={`${label(key)} (${key === selected ? total : key === 'expiring' ? expiringQuery.data?.totalElements || 0 : key === 'restricted' ? (readOnlyQuery.data?.totalElements || 0) + (suspendedQuery.data?.totalElements || 0) : key === 'inactive' ? inactiveQuery.data?.totalElements || 0 : neverUsedQuery.data?.totalElements || 0})`} />
        ))}
      </Tabs>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(error, t('dashboard.attentionLoadError'))}</Alert>}
      {isLoading && <Box sx={{ display: 'grid', placeItems: 'center', p: 3 }}><CircularProgress size={28} /></Box>}
      {!isLoading && !error && rows.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3 }}>{t('dashboard.attentionEmpty')}</Typography>
      )}
      {!isLoading && !error && rows.length > 0 && (
        <Stack sx={{ mt: 1.5 }}>
          {rows.map(company => (
            <Stack
              key={company.id}
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
              sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
            >
              <Box>
                <Typography fontWeight={700}>{company.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selected === 'expiring' || selected === 'restricted'
                    ? `${company.subscription?.effectiveStatus === SubscriptionStatus.SUSPENDED ? t('subscription.suspended') : t('subscription.readOnly')} · ${company.subscription?.endsAt ? `${t('dashboard.attentionEndsAt')}: ${formatDate(company.subscription.endsAt)}` : t('companies.unlimited')}`
                    : selected === 'inactive'
                      ? `${t('dashboard.attentionLastActivity')}: ${formatDate(company.lastBusinessActivityAt)}`
                      : `${t('companies.createdAt')}: ${formatDate(company.createdAt)}`}
                </Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate(`/companies/${company.id}`)}>
                {t('dashboard.attentionOpen')}
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default CompanyAttentionList;
